import { FC, memo, useCallback } from 'react'
import s from './BuildRadio.module.sass'
import { InputTitle } from '@shared/ui/Form/Single/InputTitle'
import { InputHint } from '@shared/ui/Form/Single/InputHint'
import { useFieldHint } from '@shared/lib/fields/hooks/useFieldHint/useFieldHint'
import { Radio } from '@shared/ui/Form/Single/Radio'
import { FieldRadio, RadioOption } from '@shared/types/fields/fields'
import { BlockBoundary } from '../../../../lib/components/BlockBoundary'

interface Props {
  field: FieldRadio
  setField: React.Dispatch<React.SetStateAction<FieldRadio>>

  handleChange?: (value: RadioOption) => void
  className?: string
  mode?: 'checkbox' | 'button' | 'radio'
  hideTitle?: boolean
}

const BuildRadio: FC<Props & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>> = ({
  field,
  setField,
  handleChange,
  className = '',
  mode = 'checkbox',
  hideTitle,
  ...props
}) => {
  const [hint] = useFieldHint(field)

  const radioGroupClass = mode === 'radio' ? s['radio-group'] : null
  const buttonsClass = field.settings.options.length > 2 ? s.buttons : null
  const groupClass = radioGroupClass ?? buttonsClass ?? s.mutant

  const handleClick = useCallback(
    (option: RadioOption) => {
      setField((prev) => ({
        ...prev,
        error: '',
        value: option.id,
      }))
      handleChange && handleChange(option)
    },
    [handleChange, setField],
  )

  return (
    <BlockBoundary>
      {field.visible && (
        <div className={`${s.wrapper} ${className}`} {...props}>
          {!hideTitle && (
            <InputTitle
              nameField={field.name}
              title={field.settings.label}
              showRequired={field.required && field.settings.showRequired}
            />
          )}

          <div className={groupClass}>
            {field.settings.options.map((option) => (
              <Radio
                key={option.id}
                option={option}
                handleClick={handleClick}
                checked={field.value === option.id}
                mode={mode}
                className={field.value === option.id ? s.active : ''}
              />
            ))}
          </div>

          <InputHint text={hint.text} status={hint.status} />
        </div>
      )}
    </BlockBoundary>
  )
}

export default memo(BuildRadio)
